import { Injectable, Injector, inject } from '@angular/core';
import { DynamicFormV3UtilsService } from '@bepatient/common';
import {
  CoreActions,
  CoreActionSetMenuReloadRequired,
  CoreActionsTypes,
  CoreBackendIDs,
  CoreMobiResourceType,
  CorePageModuleIDs,
  CoreRouterActionNavigateToPageModule,
  CoreRouterActions,
  CoreRouterNavigationIntent,
  CoreUtilsService,
  CORE_BACKEND,
  CoreActionDisplayFormErrors,
  selectorCoreFormCurrentPageStatus,
  selectorCoreHttpError,
  selectorCoreInstanceInfo,
  selectorCoreNavigateToResourceStackLastItemType,
  selectorCoreNextActivity,
  CoreRouterActionNavigateRoot,
  CoreActionLoadedFormData,
  selectorCoreDeeplinkFormData,
  CoreActionSaveDeeplinkFormData
} from '@bepatient/core';
import { SharedPopupService, SharedUtilsService } from '@bepatient/shared';
import { AlertController, ToastController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, forkJoin } from 'rxjs';
import {
  catchError,
  delay,
  filter,
  map,
  mapTo,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import {
  MyFormActionFormCompleted,
  MyFormActionLoadFormFailed,
  MyFormActionLoadFormSuccess,
  MyFormActions,
  MyFormActionSaveCurrentPageFailed,
  MyFormActionSaveCurrentPageSuccess,
  MyFormActionSetFormInvalidFields,
  MyFormActionTypes
} from './my-form-store.actions';
import { selectorMyFormForm } from './my-form-store.reducer';

const enum FormAlertTypes {
  ID = 'Invalid Form'
}
@Injectable()
export class MyFormStoreEffects {
  private actions$ = inject(Actions);
  private store$ = inject<Store<any>>(Store);
  private formV3Utils = inject(DynamicFormV3UtilsService);
  private alertController = inject(AlertController);
  private toastController = inject(ToastController);
  private translate = inject(TranslateService);
  private injector = inject(Injector);
  private sharedPopupService = inject(SharedPopupService);
  private coreUtilsService = inject(CoreUtilsService);
  private sharedUtilsService = inject(SharedUtilsService);

  private alerts: { [id: string]: HTMLIonAlertElement } = {};

  setFormUUID$: Observable<CoreRouterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(MyFormActionTypes.SET_FORM_UUID),
      mapTo(
        new CoreRouterActionNavigateToPageModule({
          id: CorePageModuleIDs.MY_FORM,
          intent: CoreRouterNavigationIntent.FORWARD
        })
      )
    )
  );

  loadForm$: Observable<MyFormActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(MyFormActionTypes.LOAD_FORM),
      withLatestFrom(
        this.store$.select(
          selectorCoreNavigateToResourceStackLastItemType(
            CoreMobiResourceType.FORM
          )
        ),
        (action, item) => item?.href
      ),
      filter(href => !!href),
      switchMap(href => {
        const match = href.match(
          /(forms|profile)\/(\w+)(\/answers\/(\w+))?(\?occurrence=\w+)/
        );
        const url =
          this.injector.get(CORE_BACKEND) === CoreBackendIDs.SMART
            ? '/rest/contents/forms/'
            : '/api/contents/forms/';
        if (match && (match.length === 5 || match.length === 6)) {
          return this.formV3Utils
            .loadFormFromMOBI(
              `${url}${match[2]}${
                match.length === 6 && match[5] ? match[5] : ''
              }`,
              match[4]
            )
            .pipe(
              map(res => {
                this.store$.dispatch(new CoreActionLoadedFormData(res));
                return new MyFormActionLoadFormSuccess(res);
              }),
              catchError(e => {
                return of(new MyFormActionLoadFormFailed());
              })
            );
        }
      })
    )
  );

  saveCurrentPage$: Observable<MyFormActions | any> = createEffect(() =>
    this.actions$.pipe(
      ofType(MyFormActionTypes.SAVE_CURRENT_PAGE),
      map(() => {
        let pageInfo: any;
        this.store$
          .pipe(select(selectorCoreFormCurrentPageStatus), take(1))
          .subscribe(v => {
            pageInfo = v;
            if (!pageInfo.status) {
              let message: string;
              let color: string;

              this.store$
                .pipe(select(selectorMyFormForm), take(1))
                .subscribe(form => {
                  // const requiredErrorLength = pageInfo.invalidFields.filter(
                  //   invalid =>
                  //     !!invalid.requiredCount && invalid.requiredCount > 0
                  // ).length;
                  // const maxLengthErrorLength = pageInfo.invalidFields.filter(
                  //   invalid => !!invalid.maxLenCount && invalid.maxLenCount > 0
                  // ).length;

                  // const limitErrorLength = pageInfo.invalidFields.filter(
                  //   invalid => !!invalid.limitCount && invalid.limitCount > 0
                  // ).length;

                  // const photoSizeErrorLength = pageInfo.invalidFields.filter(
                  //   invalid =>
                  //     !!invalid.photoSizeLimitCount &&
                  //     invalid.photoSizeLimitCount > 0
                  // ).length;

                  // if (
                  //   requiredErrorLength > 0 ||
                  //   maxLengthErrorLength > 0 ||
                  //   limitErrorLength > 0 ||
                  //   photoSizeErrorLength > 0
                  // ) {
                  //   message = form.messagePopupFormFieldError;
                  // }
                  message = form.messagePopupFormFieldError;
                })
                .unsubscribe();

              this.store$
                .select(selectorCoreInstanceInfo)
                .pipe(take(1))
                .subscribe(instanceInfo => {
                  color = instanceInfo && instanceInfo.appColor;
                })
                .unsubscribe();

              this.alertController
                .create({
                  message: message,
                  cssClass: 'bepatient-notification-alert',
                  mode: 'ios',
                  backdropDismiss: false,
                  buttons: [
                    {
                      text: this.translate.instant('OK'),
                      cssClass: ['default'],
                      handler: () => {
                        this.store$.dispatch(
                          new MyFormActionSetFormInvalidFields(
                            pageInfo.invalidFields
                          )
                        );
                        this.store$.dispatch(
                          new CoreActionDisplayFormErrors(true)
                        );
                        return true;
                      }
                    }
                  ]
                })
                .then(alert => {
                  alert.present();
                  if (color) {
                    alert.style.setProperty(
                      '--bepatient-shared-alert-color',
                      color
                    );
                  }
                  this.alerts[FormAlertTypes.ID] = alert;
                });
            }
          })
          .unsubscribe();
        return pageInfo;
      }),
      filter(pageInfo => !!pageInfo.status),
      withLatestFrom(this.store$.select(selectorMyFormForm)),
      switchMap(([pageInfo, form]) =>
        this.formV3Utils.saveFormToMOBI(form).pipe(
          map(() => {
            let noMorePages: boolean = false;
            let pagesAfterCurrentPage: Array<boolean>;
            // when current page is not the last page, check page skip status
            if (
              form &&
              form.skip &&
              form.skip.length > 0 &&
              form.currentPage < form.questionPages.length - 1
            ) {
              // Get all pages skip status after current page
              pagesAfterCurrentPage = form.skip.slice(form.currentPage + 1);
              // Check if all the pages after current page are skipped and set noMorePages true/false
              noMorePages = pagesAfterCurrentPage.every(
                skipStatus => skipStatus === true
              );
            }
            /**
             * If current page is last page, OR current page is not last page and all pages after current page are skipped
             * then dispatch form completed
             */
            const isCompleted =
              form.currentPage === form.questionPages.length - 1 ||
              noMorePages == true;
            return isCompleted
              ? new MyFormActionFormCompleted({
                  uuid: form.uuid,
                  occurrence: form.intPageOccurrence
                    ? Number(form.intPageOccurrence)
                    : 1
                })
              : new MyFormActionSaveCurrentPageSuccess();
          }),
          catchError(invalidFields =>
            of(
              invalidFields
                ? new MyFormActionSetFormInvalidFields(invalidFields)
                : new MyFormActionSaveCurrentPageFailed()
            )
          )
        )
      )
    )
  );

  saveCurrentPageSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          MyFormActionTypes.SAVE_CURRENT_PAGE_SUCCESS,
          MyFormActionTypes.PREV_PAGE
        ),
        withLatestFrom(this.store$.select(selectorMyFormForm)),
        tap(([, form]) => {
          this.store$.dispatch(new CoreActionDisplayFormErrors(false));
          this.formV3Utils.updatePageData(form);
        })
      ),
    { dispatch: false }
  );

  saveCurrentPageFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MyFormActionTypes.SAVE_CURRENT_PAGE_FAILED),
        withLatestFrom(this.store$.select(selectorCoreHttpError)),
        tap(([, error]) => {
          if (error && error.status) {
            this.alertController
              .create({
                header:
                  error && error.statusText && error.statusText != 'OK'
                    ? error.statusText
                    : void 0,
                message: error.message,
                cssClass: 'bepatient_dynamic_global_alert',
                mode: 'ios',
                backdropDismiss: false,
                buttons: [
                  {
                    text: this.translate.instant('OK'),
                    cssClass: ['default']
                  }
                ]
              })
              .then(alert => {
                alert.present();
              });
          }
        })
      ),
    { dispatch: false }
  );

  setFormInvalidFields$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MyFormActionTypes.SET_FORM_INVALID_FIELDS),
        withLatestFrom(this.store$.select(selectorCoreHttpError)),
        switchMap(() =>
          forkJoin([
            this.coreUtilsService.getSafeTranslations([
              'MY_FORM.ALERT_ERRORS.HEADER',
              'MY_FORM.ALERT_ERRORS.MESSAGE'
            ]),
            this.store$.select(selectorCoreInstanceInfo).pipe(take(1)),
            this.store$.select(selectorCoreFormCurrentPageStatus).pipe(take(1))
          ])
        ),
        map(([translations, instanceInfo, pageInfo]) => {
          if (pageInfo.status) {
            this.sharedPopupService.simpleAlert({
              color: instanceInfo && instanceInfo.appColor,
              title: translations[0],
              message: translations[1],
              buttons: [this.translate.instant('OK')]
            });
          }
        })
      ),
    { dispatch: false }
  );

  formCompleted$: Observable<CoreRouterActions | CoreActions> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MyFormActionTypes.FORM_COMPLETED),
        map((action: MyFormActionFormCompleted) => action.payload),
        map(() => {
          // MYFSHD: Reset the deeplink form data after form completely saved.
          this.store$.dispatch(new CoreActionSaveDeeplinkFormData(void 0));

          let nextActivity: boolean;
          this.store$
            .pipe(select(selectorCoreNextActivity), take(1))
            .subscribe(v => {
              nextActivity = v;
            });
          return !nextActivity;
        }),
        filter(activity => activity),
        mergeMap(() => {
          return [
            new CoreActionSetMenuReloadRequired(true),
            new CoreRouterActionNavigateRoot({
              url: 'timeline'
            })
          ];
        })
      )
  );

  formCompletedToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MyFormActionTypes.FORM_COMPLETED),
        withLatestFrom(this.store$.select(selectorMyFormForm)),
        delay(500),
        tap(([, myForm]) => {
          this.toastController
            .create({
              message: `${myForm.title} ${this.translate.instant(
                'MY_FORM.SAVED_TOAST.MESSAGE'
              )}`,
              position: 'top',
              duration: 2000,
              cssClass: 'bepatient_dynamic_form_save_toast'
            })
            .then(alert => alert.present());
        })
      ),
    { dispatch: false }
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.LOGOUT),
        tap(() => {
          if (this.alerts[FormAlertTypes.ID]) {
            this.alerts[FormAlertTypes.ID].dismiss();
            delete this.alerts[FormAlertTypes.ID];
          }
        })
      ),
    { dispatch: false }
  );
}
